import { create } from 'zustand';
import type {
  AgentConnectionState,
  DocumentField,
  DocumentCompletion,
  Gap,
  SmartGap,
  GapGroup,
  GapFilterOptions,
  GapStats,
  AgentSummary,
  ProcessingNode,
  ProcessingStatus,
  FieldSuggestion,
} from '@/types/agent.types';
import {
  createSmartGap,
  groupGapsBySection,
  calculateGapStats,
  filterGaps,
  sortGaps,
  getUniqueSections,
  findGapsToAutoResolve,
} from '@/services/gapAggregator';

// Default filter options
const defaultFilters: GapFilterOptions = {
  sections: [],
  priorities: [],
  showResolved: false,
  searchQuery: '',
  sortBy: 'priority',
  sortOrder: 'desc',
};

// Node labels in Portuguese
const nodeLabels: Record<ProcessingNode, string> = {
  analyze_chunk: 'Analisando transcrição...',
  update_document: 'Extraindo campos...',
  identify_gaps: 'Detectando lacunas...',
  generate_summary: 'Gerando resumo...',
  idle: '',
};

// Default processing status
const defaultProcessingStatus: ProcessingStatus = {
  currentNode: 'idle',
  nodeLabel: '',
  isProcessing: false,
  timestamp: '',
};

interface AgentState {
  // Connection state
  connectionState: AgentConnectionState;

  // Document fields
  documentFields: DocumentField[];
  documentCompletion: DocumentCompletion | null;

  // Smart Gaps with aggregation
  smartGaps: SmartGap[];
  gapFilters: GapFilterOptions;

  // Field suggestions (pending approval)
  pendingSuggestions: FieldSuggestion[];

  // Summary
  summary: AgentSummary | null;

  // Meeting ending state
  isEndingMeeting: boolean;
  meetingEndingCountdown: number | null;  // Countdown in seconds (null = not ending)

  // Processing status for agent feedback
  processingStatus: ProcessingStatus;

  // Errors
  lastError: string | null;

  // Actions
  setConnectionState: (state: AgentConnectionState) => void;
  updateDocumentField: (field: DocumentField) => void;
  removeDocumentField: (fieldName: string) => void;
  setDocumentCompletion: (completion: DocumentCompletion) => void;

  // Suggestion actions
  addSuggestion: (suggestion: FieldSuggestion) => void;
  removeSuggestion: (suggestionId: string) => void;
  getSuggestionForGap: (gapId: string, fieldName?: string) => FieldSuggestion | undefined;
  clearSuggestions: () => void;

  // Gap actions
  addGapWithAggregation: (gap: Gap) => void;
  resolveGap: (id: string) => void;
  resolveMultipleGaps: (ids: string[]) => void;

  // Filter actions
  setGapFilters: (filters: Partial<GapFilterOptions>) => void;
  resetGapFilters: () => void;

  // Computed getters
  getFilteredGaps: () => SmartGap[];
  getGapsBySection: () => GapGroup[];
  getGapStats: () => GapStats;
  getUniqueSections: () => string[];

  // Other actions
  setSummary: (summary: AgentSummary) => void;
  setError: (error: string | null) => void;
  setIsEndingMeeting: (isEnding: boolean) => void;
  setMeetingEndingCountdown: (countdown: number | null) => void;
  setProcessingStatus: (node: ProcessingNode, isProcessing: boolean) => void;
  clearAll: () => void;
}

export const useAgentStore = create<AgentState>((set, get) => ({
  // Initial state
  connectionState: 'disconnected',
  documentFields: [],
  documentCompletion: null,
  smartGaps: [],
  gapFilters: { ...defaultFilters },
  pendingSuggestions: [],
  summary: null,
  isEndingMeeting: false,
  meetingEndingCountdown: null,
  processingStatus: { ...defaultProcessingStatus },
  lastError: null,

  // Actions
  setConnectionState: (connectionState) => set({ connectionState }),

  updateDocumentField: (field) =>
    set((state) => {
      const existingIndex = state.documentFields.findIndex(
        (f) => f.field_name === field.field_name
      );

      let updatedFields: typeof state.documentFields;
      if (existingIndex >= 0) {
        // Update existing field
        updatedFields = [...state.documentFields];
        updatedFields[existingIndex] = field;
      } else {
        // Add new field
        updatedFields = [...state.documentFields, field];
      }

      // Auto-resolve gaps related to this field
      const gapIdsToResolve = findGapsToAutoResolve(field.field_name, state.smartGaps);
      const updatedGaps = state.smartGaps.map((gap) =>
        gapIdsToResolve.includes(gap.id)
          ? { ...gap, resolved: true, autoResolvedBy: field.field_name }
          : gap
      );

      return {
        documentFields: updatedFields,
        smartGaps: updatedGaps,
      };
    }),

  removeDocumentField: (fieldName: string) =>
    set((state) => {
      // Remove field from documentFields
      const updatedFields = state.documentFields.filter(
        (f) => f.field_name !== fieldName
      );

      // Un-resolve gaps that were auto-resolved by this field
      const updatedGaps = state.smartGaps.map((gap) =>
        gap.autoResolvedBy === fieldName || gap.field_name === fieldName
          ? { ...gap, resolved: false, autoResolvedBy: undefined }
          : gap
      );

      return {
        documentFields: updatedFields,
        smartGaps: updatedGaps,
      };
    }),

  // Suggestion actions
  addSuggestion: (suggestion) =>
    set((state) => {
      console.log('[AgentStore] addSuggestion called:', {
        new: suggestion,
        existing: state.pendingSuggestions.map(s => ({
          id: s.suggestionId,
          field: s.fieldName
        }))
      });

      // PRIMEIRO: Verificar se existe sugestão para o mesmo campo
      // (deve vir antes da verificação de ID para permitir substituição por valor melhor)
      const existingIndex = state.pendingSuggestions.findIndex(
        (s) => s.fieldName === suggestion.fieldName
      );

      if (existingIndex >= 0) {
        const existing = state.pendingSuggestions[existingIndex];
        const existingWords = (existing.proposedValue?.trim() || '').split(/\s+/).length;
        const newWords = (suggestion.proposedValue?.trim() || '').split(/\s+/).length;

        // Substituir se nova sugestão tem mais informação
        if (newWords > existingWords) {
          console.log('[AgentStore] Replacing suggestion with more complete value:', suggestion.fieldName);
          const updatedSuggestions = [...state.pendingSuggestions];
          updatedSuggestions[existingIndex] = suggestion;
          return { pendingSuggestions: updatedSuggestions };
        } else {
          console.log('[AgentStore] Keeping existing suggestion (not more complete):', suggestion.fieldName);
          return state;
        }
      }

      // DEPOIS: Verificar duplicado por ID (apenas para campos diferentes)
      const existsById = state.pendingSuggestions.some(
        (s) => s.suggestionId === suggestion.suggestionId
      );
      if (existsById) {
        console.log('[AgentStore] Skipping duplicate by ID:', suggestion.suggestionId);
        return state;
      }

      // Adicionar nova sugestão
      console.log('[AgentStore] Adding new suggestion:', suggestion.suggestionId, suggestion.fieldName);
      return {
        pendingSuggestions: [...state.pendingSuggestions, suggestion],
      };
    }),

  removeSuggestion: (suggestionId) =>
    set((state) => ({
      pendingSuggestions: state.pendingSuggestions.filter(
        (s) => s.suggestionId !== suggestionId
      ),
    })),

  getSuggestionForGap: (gapId, fieldName?: string) => {
    const state = get();
    // First try matching by relatedGapId
    const byGapId = state.pendingSuggestions.find((s) => s.relatedGapId === gapId);
    if (byGapId) return byGapId;

    // Fallback: match by fieldName if provided
    if (fieldName) {
      return state.pendingSuggestions.find((s) => s.fieldName === fieldName);
    }
    return undefined;
  },

  clearSuggestions: () => set({ pendingSuggestions: [] }),

  setDocumentCompletion: (completion) =>
    set({ documentCompletion: completion }),

  // Add gap (no aggregation needed - gaps come from template fields)
  addGapWithAggregation: (gap) =>
    set((state) => {
      // Skip if gap with same ID already exists
      const exists = state.smartGaps.some((g) => g.id === gap.id);
      if (exists) {
        return state;
      }

      // Add new gap
      const smartGap = createSmartGap(gap);
      return { smartGaps: [...state.smartGaps, smartGap] };
    }),

  resolveGap: (id) =>
    set((state) => ({
      smartGaps: state.smartGaps.map((g) =>
        g.id === id ? { ...g, resolved: true } : g
      ),
      // Remove suggestions related to the resolved gap
      pendingSuggestions: state.pendingSuggestions.filter(
        (s) => s.relatedGapId !== id
      ),
    })),

  resolveMultipleGaps: (ids) =>
    set((state) => ({
      smartGaps: state.smartGaps.map((g) =>
        ids.includes(g.id) ? { ...g, resolved: true } : g
      ),
      // Remove suggestions related to resolved gaps
      pendingSuggestions: state.pendingSuggestions.filter(
        (s) => !s.relatedGapId || !ids.includes(s.relatedGapId)
      ),
    })),

  // Filter actions
  setGapFilters: (filters) =>
    set((state) => ({
      gapFilters: { ...state.gapFilters, ...filters },
    })),

  resetGapFilters: () =>
    set({ gapFilters: { ...defaultFilters } }),

  // Computed getters
  getFilteredGaps: () => {
    const state = get();
    const filtered = filterGaps(state.smartGaps, {
      sections: state.gapFilters.sections,
      priorities: state.gapFilters.priorities,
      showResolved: state.gapFilters.showResolved,
      searchQuery: state.gapFilters.searchQuery,
    });
    return sortGaps(filtered, state.gapFilters.sortBy, state.gapFilters.sortOrder);
  },

  getGapsBySection: () => {
    const state = get();
    const filtered = filterGaps(state.smartGaps, {
      sections: state.gapFilters.sections,
      priorities: state.gapFilters.priorities,
      showResolved: state.gapFilters.showResolved,
      searchQuery: state.gapFilters.searchQuery,
    });
    return groupGapsBySection(filtered);
  },

  getGapStats: () => {
    const state = get();
    return calculateGapStats(state.smartGaps);
  },

  getUniqueSections: () => {
    const state = get();
    return getUniqueSections(state.smartGaps);
  },

  // Other actions
  setSummary: (summary) => set({ summary }),

  setError: (lastError) => set({ lastError }),

  setIsEndingMeeting: (isEndingMeeting) => set({ isEndingMeeting }),

  setMeetingEndingCountdown: (meetingEndingCountdown) => set({ meetingEndingCountdown }),

  setProcessingStatus: (node, isProcessing) =>
    set({
      processingStatus: {
        currentNode: node,
        nodeLabel: isProcessing ? nodeLabels[node] : '',
        isProcessing,
        timestamp: new Date().toISOString(),
      },
    }),

  clearAll: () =>
    set({
      connectionState: 'disconnected',
      documentFields: [],
      documentCompletion: null,
      smartGaps: [],
      gapFilters: { ...defaultFilters },
      pendingSuggestions: [],
      summary: null,
      isEndingMeeting: false,
      meetingEndingCountdown: null,
      processingStatus: { ...defaultProcessingStatus },
      lastError: null,
    }),
}));
